import { useEffect, useRef, useState } from 'react';
import { STATUS_COLOR_PALETTE, type TaskStatusRow, type TaskStatusKey } from '@ehr/shared';
import { api } from '../lib/api.js';
import { taskStatusesChanged } from '../lib/taskStatuses.js';

/**
 * Task Statuses manager (V38 Settings → Task Statuses). The four built-in task
 * statuses are label/colour/order-editable — no add or delete, since Task.status
 * stays a fixed enum. Reuses the .status-mgr-* / .cp-* styling; edits persist to
 * /task-statuses.
 */

const isHex = (s: string) => /^#[0-9a-fA-F]{6}$/.test(s);
const HANDLE = '⁙⁙';

const Pencil = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

export function TaskStatusesManager({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<TaskStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<TaskStatusKey | null>(null);
  const [draft, setDraft] = useState<{ label: string; color: string }>({
    label: '',
    color: '#527060',
  });
  const dragKey = useRef<TaskStatusKey | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<TaskStatusRow[]>('/task-statuses')
      .then((r) => {
        setRows(Array.isArray(r) ? r : []);
        setErr(null);
      })
      .catch(() => setErr('Could not load task statuses. Please try again.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const startEdit = (r: TaskStatusRow) => {
    setEditingKey(r.key);
    setDraft({ label: r.label, color: r.color });
  };
  const cancelEdit = () => setEditingKey(null);
  const saveEdit = (r: TaskStatusRow) => {
    const label = draft.label.trim();
    if (!label) return;
    if (!isHex(draft.color)) {
      setErr('Enter a valid color like #1a8a66.');
      return;
    }
    api
      .patch(`/task-statuses/${r.key}`, { label, color: draft.color })
      .then(() => {
        setEditingKey(null);
        load();
        taskStatusesChanged(); // ehr#411: open task screens take the new name / colour at once
      })
      .catch(() => setErr('Could not save the task status.'));
  };

  const reorder = (targetKey: TaskStatusKey) => {
    const dKey = dragKey.current;
    dragKey.current = null;
    if (!dKey || dKey === targetKey) return;
    const next = rows.slice();
    const di = next.findIndex((r) => r.key === dKey);
    if (di < 0) return;
    const [moved] = next.splice(di, 1);
    const ti = next.findIndex((r) => r.key === targetKey);
    next.splice(ti < 0 ? next.length : ti, 0, moved!);
    setRows(next);
    api
      .patch('/task-statuses/reorder', { keys: next.map((r) => r.key) })
      .then(() => taskStatusesChanged()) // ehr#411: the board's columns follow the new order
      .catch(() => {
        setErr('Could not reorder.');
        load();
      });
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-lg" role="dialog" aria-modal="true" aria-label="Task Statuses">
        <div className="modal-head">
          <div>
            <h2>Task Statuses</h2>
            <p>Rename and recolor the statuses used on tasks. Drag to reorder.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {err && <div className="config-err">{err}</div>}
          {loading ? (
            <p className="muted">Loading…</p>
          ) : (
            <div className="status-mgr-list">
              {rows.map((r) =>
                editingKey === r.key ? (
                  <div className="status-mgr-form" key={r.key}>
                    <div className="status-mgr-form-grid">
                      <div className="field field-full">
                        <label>Label</label>
                        <input
                          type="text"
                          value={draft.label}
                          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                          onKeyDown={(e) => e.key === 'Enter' && saveEdit(r)}
                        />
                      </div>
                      <div className="field field-full">
                        <label>Color</label>
                        <div className="cp-grid cp-grid-sm">
                          {STATUS_COLOR_PALETTE.map((c, i) => (
                            <button
                              key={c + i}
                              type="button"
                              className={`cp-sw${draft.color.toLowerCase() === c.toLowerCase() ? ' sel' : ''}`}
                              style={{ background: c }}
                              title={c}
                              onClick={() => setDraft({ ...draft, color: c })}
                            />
                          ))}
                        </div>
                        <div className="cp-custom" style={{ marginTop: 9 }}>
                          <label
                            className="cp-native"
                            style={{ background: draft.color }}
                            title="Spectrum"
                          >
                            <input
                              type="color"
                              value={isHex(draft.color) ? draft.color : '#527060'}
                              onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                            />
                          </label>
                          <input
                            className="cp-hex"
                            value={draft.color}
                            maxLength={7}
                            placeholder="#527060"
                            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn" onClick={cancelEdit}>
                        Cancel
                      </button>
                      <button className="btn primary" onClick={() => saveEdit(r)}>
                        Save changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="status-mgr-row"
                    key={r.key}
                    draggable
                    onDragStart={(e) => {
                      dragKey.current = r.key;
                      e.currentTarget.classList.add('dragging');
                    }}
                    onDragEnd={(e) => e.currentTarget.classList.remove('dragging')}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('drag-over');
                    }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      reorder(r.key);
                    }}
                  >
                    <span className="status-mgr-handle" aria-hidden title="Drag to reorder">
                      {HANDLE}
                    </span>
                    <span className="status-mgr-color" style={{ background: r.color }} />
                    <div className="status-mgr-main">
                      <span className="status-mgr-label">{r.label}</span>
                    </div>
                    <div className="status-mgr-actions">
                      <button
                        className="status-mgr-btn"
                        title="Edit"
                        aria-label={`Edit ${r.label}`}
                        onClick={() => startEdit(r)}
                      >
                        {Pencil}
                      </button>
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
